import React, { useState, useEffect } from 'react';
import {
  Shield, Building, Truck, Tractor, AlertTriangle, Plus,
  Calendar, Euro, FileText, Search, Filter, Bell,
  CheckCircle, XCircle, Clock, ChevronRight, RefreshCw,
  Beef, Wheat, MoreVertical, Edit, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TYPES_ICONS = {
  rc: Shield,
  decennale: Building,
  cheptel: Beef,
  batiment: Building,
  materiel: Tractor,
  flotte: Truck,
  recolte: Wheat,
  autre: FileText
};

const TYPES_COLORS = {
  rc: 'bg-blue-100 text-blue-700',
  decennale: 'bg-purple-100 text-purple-700',
  cheptel: 'bg-amber-100 text-amber-700',
  batiment: 'bg-emerald-100 text-emerald-700',
  materiel: 'bg-orange-100 text-orange-700',
  flotte: 'bg-cyan-100 text-cyan-700',
  recolte: 'bg-lime-100 text-lime-700',
  autre: 'bg-gray-100 text-gray-700'
};

const TYPES_NOMS = {
  rc: 'Responsabilité Civile',
  decennale: 'Décennale',
  cheptel: 'Cheptel',
  batiment: 'Bâtiment',
  materiel: 'Matériel',
  flotte: 'Flotte',
  recolte: 'Récolte',
  autre: 'Autre'
};

export default function AssurancesPage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [assurances, setAssurances] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAssurance, setEditingAssurance] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchAssurances();
    fetchAlertes();
  }, []);

  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': localStorage.getItem('societeId'),
    'Content-Type': 'application/json'
  });

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assurances/dashboard`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Erreur dashboard:', error);
    }
  };

  const fetchAssurances = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assurances`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAssurances(data.assurances || []);
      }
    } catch (error) {
      console.error('Erreur assurances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assurances/alertes`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAlertes(data.alertes || []);
      }
    } catch (error) {
      console.error('Erreur alertes:', error);
    }
  };

  const filteredAssurances = assurances.filter(a => {
    const matchType = !filterType || a.type_assurance === filterType;
    const matchSearch = !searchTerm || 
      a.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assureur?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchType && matchSearch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatMoney = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (date - now) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff >= 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="assurances-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl text-white">
            <Shield className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assurances</h1>
            <p className="text-gray-500">Gérez toutes vos assurances agricoles</p>
          </div>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle assurance
        </Button>
      </div>

      {/* Alertes critiques */}
      {alertes.filter(a => a.niveau === 'critique' || a.niveau === 'urgent').length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-800">Alertes assurances</span>
          </div>
          <div className="space-y-2">
            {alertes.filter(a => a.niveau === 'critique' || a.niveau === 'urgent').slice(0, 3).map((alerte, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                <span className="text-sm text-red-700">{alerte.message}</span>
                <Button variant="ghost" size="sm" className="text-red-600">
                  Voir <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {['dashboard', 'liste', 'flottes', 'devis'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === tab 
                ? 'text-emerald-600 border-b-2 border-emerald-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'dashboard' ? 'Tableau de bord' : 
             tab === 'liste' ? 'Toutes les assurances' :
             tab === 'flottes' ? 'Flottes' : 'Demandes de devis'}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Stats globales */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-sm text-gray-500">Total assurances</p>
              <p className="text-3xl font-bold text-gray-900">{dashboard.stats?.total_assurances || 0}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-sm text-gray-500">Actives</p>
              <p className="text-3xl font-bold text-emerald-600">{dashboard.stats?.actives || 0}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-sm text-gray-500">Primes annuelles</p>
              <p className="text-3xl font-bold text-blue-600">{formatMoney(dashboard.stats?.total_primes_annuelles)}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-sm text-gray-500">Alertes</p>
              <p className="text-3xl font-bold text-red-600">
                {(dashboard.nb_alertes_critiques || 0) + (dashboard.nb_alertes_urgentes || 0)}
              </p>
            </div>
          </div>

          {/* Par type */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Par type d'assurance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(dashboard.stats?.par_type || {}).map(([type, data]) => {
                const Icon = TYPES_ICONS[type] || FileText;
                return (
                  <div key={type} className={`p-4 rounded-lg ${TYPES_COLORS[type] || 'bg-gray-100'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{TYPES_NOMS[type] || type}</span>
                    </div>
                    <p className="text-2xl font-bold">{data.count}</p>
                    <p className="text-sm opacity-75">{formatMoney(data.total_primes)}/an</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Alertes récentes */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Échéances à venir
            </h2>
            {dashboard.alertes?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.alertes.map((alerte, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                    alerte.niveau === 'critique' ? 'bg-red-50' :
                    alerte.niveau === 'urgent' ? 'bg-amber-50' : 'bg-blue-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {alerte.niveau === 'critique' ? <XCircle className="w-5 h-5 text-red-500" /> :
                       alerte.niveau === 'urgent' ? <AlertTriangle className="w-5 h-5 text-amber-500" /> :
                       <Clock className="w-5 h-5 text-blue-500" />}
                      <div>
                        <p className="font-medium">{alerte.nom}</p>
                        <p className="text-sm text-gray-500">{alerte.message}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${TYPES_COLORS[alerte.type_assurance]}`}>
                      {TYPES_NOMS[alerte.type_assurance]}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune alerte pour le moment</p>
            )}
          </div>
        </div>
      )}

      {/* Liste Tab */}
      {activeTab === 'liste' && (
        <div>
          {/* Filtres */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher une assurance..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">Tous les types</option>
              {Object.entries(TYPES_NOMS).map(([key, nom]) => (
                <option key={key} value={key}>{nom}</option>
              ))}
            </select>
          </div>

          {/* Liste */}
          {filteredAssurances.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">Aucune assurance trouvée</p>
              <Button onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une assurance
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredAssurances.map(assurance => {
                const Icon = TYPES_ICONS[assurance.type_assurance] || FileText;
                const expired = isExpired(assurance.date_fin);
                const expiringSoon = isExpiringSoon(assurance.date_fin);
                
                return (
                  <div 
                    key={assurance.id}
                    className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
                      expired ? 'border-red-300 bg-red-50' :
                      expiringSoon ? 'border-amber-300 bg-amber-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${TYPES_COLORS[assurance.type_assurance]}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{assurance.nom}</h3>
                            {assurance.tous_risques && (
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                Tous risques
                              </span>
                            )}
                            {expired && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                Expirée
                              </span>
                            )}
                            {expiringSoon && !expired && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                À renouveler
                              </span>
                            )}
                          </div>
                          <p className="text-gray-500">{assurance.assureur}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(assurance.date_debut)} → {formatDate(assurance.date_fin)}
                            </span>
                            {assurance.numero_contrat && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                N° {assurance.numero_contrat}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          {formatMoney(assurance.prime_annuelle)}
                        </p>
                        <p className="text-sm text-gray-500">/an</p>
                        <div className="flex gap-2 mt-2">
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Flottes Tab */}
      {activeTab === 'flottes' && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Gestion des flottes</p>
          <p className="text-sm text-gray-400 mb-4">Regroupez vos matériels en flottes pour une assurance commune</p>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Créer une flotte
          </Button>
        </div>
      )}

      {/* Devis Tab */}
      {activeTab === 'devis' && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Demandes de devis</p>
          <p className="text-sm text-gray-400 mb-4">L'agent IA peut rechercher les meilleures offres pour vous</p>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Demander un devis
          </Button>
        </div>
      )}

      {/* Modal création */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-assurance-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Nouvelle assurance</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const body = {
                type_assurance: formData.get('type_assurance'),
                nom: formData.get('nom'),
                assureur: formData.get('assureur'),
                numero_contrat: formData.get('numero_contrat') || null,
                date_debut: formData.get('date_debut'),
                date_fin: formData.get('date_fin'),
                prime_annuelle: parseFloat(formData.get('prime_annuelle')) || null,
                franchise: parseFloat(formData.get('franchise')) || null,
                tous_risques: formData.get('tous_risques') === 'on',
                notes: formData.get('notes') || null,
                garanties: [],
              };
              try {
                const res = await fetch(`${API_URL}/api/assurances`, {
                  method: 'POST',
                  headers: getHeaders(),
                  body: JSON.stringify(body)
                });
                if (res.ok) {
                  setShowModal(false);
                  fetchAssurances();
                  fetchDashboard();
                  fetchAlertes();
                }
              } catch (err) { console.error(err); }
            }} className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Type d'assurance *</label>
                <select name="type_assurance" required className="w-full px-3 py-2 border rounded-lg">
                  {Object.entries(TYPES_NOMS).map(([key, nom]) => (
                    <option key={key} value={key}>{nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Nom de l'assurance *</label>
                <Input name="nom" required placeholder="Ex: RC Exploitation 2025" data-testid="assurance-nom-input" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Assureur *</label>
                <Input name="assureur" required placeholder="Ex: Groupama" data-testid="assurance-assureur-input" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">N° de contrat</label>
                <Input name="numero_contrat" placeholder="Ex: GR-2025-123456" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Date début *</label>
                  <Input type="date" name="date_debut" required data-testid="assurance-date-debut" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Date fin *</label>
                  <Input type="date" name="date_fin" required data-testid="assurance-date-fin" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Prime annuelle</label>
                  <Input type="number" step="0.01" name="prime_annuelle" placeholder="0.00" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Franchise</label>
                  <Input type="number" step="0.01" name="franchise" placeholder="0.00" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="tous_risques" id="tous_risques" className="rounded" />
                <label htmlFor="tous_risques" className="text-sm">Tous risques</label>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Notes</label>
                <textarea name="notes" rows="2" className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Notes optionnelles..." />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-assurance-btn">Enregistrer</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
